// Column-header context menu. Right-click on any data-table header
// opens this floating menu anchored to the cursor; lets the operator
// sort / hide / filter / show-distribution / group-by on that column
// without diving into the filter rail. Extracted from DataSurfaceV3
// during the agent-velocity refactor — all behaviour stays in the
// parent (this is a pure render + dispatcher).

import * as React from 'react';
import type { FilterRow } from '../v2/recipe-sql';
import { NON_FILTERABLE_CELLS, ctxMenuItemStyle } from './data-surface-utils';

type ToastApi = {
  show: (
    msg: React.ReactNode,
    opts?: { kind?: 'success' | 'info' | 'error' | 'warning'; ttlMs?: number | null },
  ) => void;
};

export function ColumnHeaderMenu({
  state,
  sortColumn,
  setSortColumn,
  setSortDir,
  setDistributionField,
  setGroupByField,
  toggleColumnHidden,
  setFilters,
  setHeaderMenu,
  toast,
}: {
  state: { col: string; left: number; top: number } | null;
  sortColumn: string | null;
  setSortColumn: (v: string | null) => void;
  setSortDir: (v: 'ASC' | 'DESC') => void;
  setDistributionField: (v: string | null) => void;
  setGroupByField: (v: string | null) => void;
  toggleColumnHidden: (col: string) => void;
  setFilters: React.Dispatch<React.SetStateAction<FilterRow[]>>;
  setHeaderMenu: (v: { col: string; left: number; top: number } | null) => void;
  toast: ToastApi;
}): React.ReactElement | null {
  if (!state) return null;
  const col = state.col;
  return (
    <div
      onMouseDown={e => e.stopPropagation()}
      style={{
        position: 'fixed',
        left: Math.min(state.left, window.innerWidth - 220),
        top: Math.min(state.top, window.innerHeight - 280),
        background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 6,
        boxShadow: '0 6px 20px oklch(0.18 0.01 250 / 0.20)',
        minWidth: 200, padding: 4, zIndex: 1200,
        display: 'flex', flexDirection: 'column',
      }}
    >
      <div style={{
        padding: '4px 10px', fontSize: 10.5, fontFamily: 'var(--mono)',
        textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)',
        borderBottom: '1px solid var(--line)', marginBottom: 4,
      }}>{col}</div>
      <button
        type="button"
        onClick={() => { setSortColumn(col); setSortDir('ASC'); setHeaderMenu(null); }}
        style={ctxMenuItemStyle}
      >Sort ascending ↑</button>
      <button
        type="button"
        onClick={() => { setSortColumn(col); setSortDir('DESC'); setHeaderMenu(null); }}
        style={ctxMenuItemStyle}
      >Sort descending ↓</button>
      {sortColumn === col && (
        <button
          type="button"
          onClick={() => { setSortColumn(null); setHeaderMenu(null); }}
          style={ctxMenuItemStyle}
        >Clear sort</button>
      )}
      <div style={{ borderTop: '1px solid var(--line)', margin: '4px 0' }} />
      <button
        type="button"
        onClick={() => { setDistributionField(col); setHeaderMenu(null); }}
        style={ctxMenuItemStyle}
        title="Side drawer with this column's value distribution + click-to-filter"
      >📊 Show distribution</button>
      <button
        type="button"
        onClick={() => { setGroupByField(col); setHeaderMenu(null); }}
        style={ctxMenuItemStyle}
        title="Replace the table with a count per unique value of this column"
      >📊 Group by this field</button>
      <div style={{ borderTop: '1px solid var(--line)', margin: '4px 0' }} />
      <button
        type="button"
        onClick={() => { toggleColumnHidden(col); setHeaderMenu(null); }}
        style={ctxMenuItemStyle}
      >Hide column</button>
      {!NON_FILTERABLE_CELLS.has(col) && (
        <>
          <div style={{ borderTop: '1px solid var(--line)', margin: '4px 0' }} />
          <button
            type="button"
            onClick={() => {
              setFilters(prev => [...prev, { field_path: col, op: 'is_present', value: '' }]);
              setHeaderMenu(null);
              toast.show(`Filter added: \`${col}\` is present`, { kind: 'success' });
            }}
            style={ctxMenuItemStyle}
          >Filter: is present</button>
          <button
            type="button"
            onClick={() => {
              setFilters(prev => [...prev, { field_path: col, op: 'is_empty', value: '' }]);
              setHeaderMenu(null);
              toast.show(`Filter added: \`${col}\` is empty`, { kind: 'success' });
            }}
            style={ctxMenuItemStyle}
          >Filter: is empty</button>
        </>
      )}
    </div>
  );
}
